import { getAdminUrl, getMergedHeaders, useReapitConnectSession } from '@reapitform/hooks'
import { AdminFormField, AdminFormFieldPayload, AdminReviewFieldDependencyPayload } from './types'

/**
 * Imperative admin-API helpers for authoring composite fields. A composite field (Select / RadioGroup /
 * boolean) is created as a parent FormField plus one option FormField per choice, each linked to the parent
 * by a dependency. The option/child references are only known at runtime (after each create), so these can't
 * use the fixed-path `useAdminUpdate` hooks inside a loop. Auth headers mirror `useAdminUpdate`.
 */
export const useFieldAuthoring = () => {
  const session = useReapitConnectSession()

  const request = async <T>(path: string, method: string, body: unknown): Promise<T> => {
    const headers = await getMergedHeaders(session)
    if (!headers) throw new Error('Your session has expired — please sign in again.')

    const response = await fetch(getAdminUrl(path)(), {
      method,
      headers: headers as HeadersInit,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`)
    }

    return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
  }

  return {
    createFormField: (sectionReferenceID: string, payload: AdminFormFieldPayload) =>
      request<AdminFormField>(`/api/review-card-sections/${sectionReferenceID}/fields`, 'POST', payload),

    updateFormField: (sectionReferenceID: string, fieldReferenceID: string, payload: AdminFormFieldPayload) =>
      request<AdminFormField>(
        `/api/review-card-sections/${sectionReferenceID}/fields/${fieldReferenceID}`,
        'PUT',
        payload,
      ),

    createDependency: (childFieldReferenceID: string, payload: AdminReviewFieldDependencyPayload) =>
      request<void>(`/api/form-fields/${childFieldReferenceID}/dependencies`, 'POST', payload),

    updateDependency: (
      childFieldReferenceID: string,
      dependencyReferenceID: string,
      payload: AdminReviewFieldDependencyPayload,
    ) =>
      request<void>(`/api/form-fields/${childFieldReferenceID}/dependencies/${dependencyReferenceID}`, 'PUT', payload),

    deleteDependency: (childFieldReferenceID: string, dependencyReferenceID: string) =>
      request<void>(
        `/api/form-fields/${childFieldReferenceID}/dependencies/${dependencyReferenceID}`,
        'DELETE',
        undefined,
      ),
  }
}
